import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Laptop, Clock, DollarSign, FileText } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do I create an invoice as a freelancer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Go to Invilic, choose "Freelancer Invoice", add your name and business details, enter your client\'s information, add your services as line items with rates, and download the PDF. Takes under 2 minutes and is completely free.',
      },
    },
    {
      '@type': 'Question',
      name: 'What should a freelancer invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A freelancer invoice should include: your name/business name and contact info; client name and address; unique invoice number; invoice date and payment due date; itemized list of services with rates and quantities; subtotal, any taxes, and the total amount due; payment terms and accepted payment methods.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is a freelancer invoice legally valid?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. A freelancer invoice is a legal document. As long as it includes the required information (your details, client details, services rendered, and amount due), it is valid for requesting payment and for tax/accounting purposes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add my logo to the freelancer invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you upload your business logo which appears at the top of the invoice PDF, making it look professional and branded.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I add payment terms to my invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Invilic includes fields for payment due date, payment terms (e.g. Net 30, Due on receipt), and a notes section where you can add bank details, UPI ID, or any additional payment instructions.',
      },
    },
  ],
};

const useCases = [
  { icon: Laptop, title: 'Developers & designers', desc: 'Bill clients for websites, apps, UI/UX work, or ongoing retainers.' },
  { icon: FileText, title: 'Writers & content creators', desc: 'Invoice for articles, copywriting, video scripts, or social media content.' },
  { icon: DollarSign, title: 'Consultants & coaches', desc: 'Issue professional invoices for consulting hours, strategy sessions, or coaching packages.' },
  { icon: Clock, title: 'Photographers & videographers', desc: 'Invoice for shoots, editing hours, licensing, or event coverage.' },
];

export default function FreelancerInvoiceGeneratorPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Freelancer Invoice Generator: Free Professional Invoice Maker | Invilic"
        description="Create professional freelancer invoices online free. Add logo, payment terms, line items. Download PDF instantly. Perfect for developers, designers, writers & consultants."
        canonical="https://www.invilic.com/freelancer-invoice-generator"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            For Freelancers
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Freelancer Invoice Generator
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Create professional freelancer invoices in minutes. Add your logo, itemize services, set payment terms, and send a polished PDF to clients, completely free.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Create Freelancer Invoice <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* What's included */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's on Your Freelancer Invoice
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name, business name & logo',
              'Client name and address',
              'Invoice number & date',
              'Payment due date',
              'Itemized services with rate × quantity',
              'Subtotal, discount, and tax',
              'Total amount due',
              'Payment terms & notes',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30 bg-blue-50 dark:bg-blue-900/10">
                <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Who uses it */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Built for Every Type of Freelancer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {useCases.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section className="mb-16 p-8 rounded-2xl border border-blue-100 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-900/20">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">Tips for Getting Paid Faster</h2>
          <ul className="space-y-3">
            {[
              'Always include a specific due date. "Net 30" is clearer than "30 days"',
              'Add your UPI ID or bank details directly on the invoice so clients can pay immediately',
              'Use sequential invoice numbers (INV-001, INV-002) for easy record keeping',
              'Send the invoice the same day you complete the work, don\'t wait',
              'Use Invilic Pro to email invoices directly from the platform and track opens',
            ].map(tip => (
              <li key={tip} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                {tip}
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Freelancer Invoice: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Freelancer Invoice Now
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF invoice in under 2 minutes. Free.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

